<template>
	<div class="nav">
		<transition name="overlay-fade">
			<div v-show="isOpen" class="nav__overlay"></div>
		</transition>
		<div class="nav__header container">
			<a href="/" class="nav__logo-home-link">
				<img :src="logoLight" alt="Interiør verkstedet theme-light" />
			</a>
		</div>

		<div @click="isOpen ? close() : open()" class="nav__hamburger" :class="{ 'nav__hamburger--open': isOpen }">
			<lottie :options="hamburgerMenuOptions" :height="40" :width="40" v-on:animCreated="handleAnimation" />
		</div>

		<transition name="content-slide">
			<div v-show="isOpen" class="nav__content-container">
				<div class="nav__content-content">
					<div class="nav__content-header">
						<a
							v-for="(link, index) in topLinks"
							:key="index + 'nav'"
							:href="$path(link, 'slug')"
							class="nav__content-top-links a-none-deco uppercase"
							>{{ link.title }}
						</a>
					</div>
					<div class="nav__content-links">
						<template v-if="responsive">
							<a
								v-for="(link, index) in links"
								:key="index + 'nav'"
								:href="$path(link, 'slug')"
								class="nav__content-link-title a-none-deco uppercase"
								>{{ link.title }}
							</a>
						</template>

						<template v-else>
							<Card
								v-for="(link, index) in links"
								:key="index + 'nav'"
								:item="link"
								cover-height="190px"
								@close="close"
							>
								<div v-if="$path(link, 'title')" class="nav__content-link-title uppercase">
									{{ link.title }}
								</div>
							</Card>
						</template>
					</div>
					<hr class="nav__content-hr" />
					<div class="nav__content-sosial-media-wrapper row">
						<template v-for="icon in icons">
							<div :key="icon.action" class="nav__content-sosial-media">
								<Icon :icon="icon" />
								<a :key="icon.title" :href="$path(icon, 'action')" class="a-none-deco">{{ icon.title }} </a>
							</div>
						</template>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import Card from '@/components/Card'
import Icon from '@/components/Icon'
import PageWidth from '@/mixins/pagewidth'
import defaultLogo from '@/assets/logo_light.svg'
import Lottie from 'vue-lottie'

import hamburgerMenuData from '../assets/hamburger-icon.json'

export default {
	mixins: [PageWidth],
	props: {
		content: {
			type: Object,
			default: () => ({}),
		},
		displayHalf: {
			type: Boolean,
			default: false,
		},
	},
	watch: {
		isOpen: {
			handler(val) {
				document.body[val ? 'setAttribute' : 'removeAttribute']('nav-open', '')

				if (!this.anim) return

				if (val) {
					this.anim.setDirection(1)
					this.anim.goToAndPlay(30, true)
				} else {
					this.anim.setDirection(-1)
					this.anim.goToAndPlay(60, true)
				}
			},
			immediate: true,
		},
	},
	data() {
		return {
			isOpen: false,
			anim: null,
			hamburgerMenuOptions: { animationData: hamburgerMenuData, autoplay: false, loop: false },
		}
	},
	computed: {
		logoLight() {
			return this.content.logoLight || defaultLogo
		},
		links() {
			if (!this.content.links) return
			let result = this.content.links.map(element => {
				return {
					title: element.title,
					url: element.thumbnail.url,
					slug: element.slug,
					focalPoint: element.thumbnail.focalPoint,
				}
			})
			return result
		},
		topLinks() {
			if (!this.content.topLinks) return
			let result = this.content.topLinks.map(element => {
				return {
					title: element.title,
					slug: element.slug,
				}
			})
			return result
		},
		icons() {
			if (!this.$path(this.content, 'icon')) return []

			return this.content.icon.map(item => {
				return {
					src: item.file.url,
					alt: item.file.alt,
					action: item.link,
					title: item.title,
				}
			})
		},
	},
	methods: {
		routeToContact() {
			this.close()
			return this.$router.push({ path: '/kontakt-oss', query: { contactCategory: 'privat' } }).catch(() => {})
		},
		close() {
			this.isOpen = false
		},
		open() {
			this.isOpen = true
		},
		handleAnimation(anim) {
			this.anim = anim
			this.anim.goToAndStop(1, true)
		},
	},
	beforeRouteLeave() {
		this.close()
	},
	components: {
		Card,
		Icon,
		Lottie,
	},
}
</script>

<style lang="scss">
@import '@/styles/base/_color.scss';
@import '@/styles/base/_variables.scss';
@import '@/styles/utils/_mixins.scss';
@import '@/styles/base/_typography.scss';

body[nav-open] {
	overflow: hidden;
}

.nav {
	color: $primary;

	&__header {
		height: $nav-height;
		top: 0;
		display: flex;
		justify-content: space-between;
		position: absolute;
		align-items: center;
		width: 100%;
		max-width: initial !important;
		z-index: get-layer('nav');
		padding-left: 2rem !important;
		padding-right: 2rem !important;

		@include respond-below('phone') {
			padding: 1.5rem 1.5rem;
		}
	}

	&__logo-home-link {
		img {
			max-height: 50px;
			max-width: 160px;
		}
	}

	&__hamburger {
		cursor: pointer;
		position: absolute;
		top: 2rem;
		right: 2rem;
		z-index: get-layer('nav-expanded-menu') + 1;

		path {
			stroke: white;
		}
		&--open {
			position: fixed;
			path {
				stroke: var(--primary);
			}
		}
	}

	&__overlay {
		position: fixed;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		background: $primary;
		opacity: 0.75;
		z-index: get-layer('nav-overlay');
	}

	&__content {
		$spaceLeft: 1.25rem;
		$space: 2.5rem;
		&-container {
			position: fixed;
			top: 0;
			right: 0;
			height: 100%;
			background: $secondary;
			width: 100%;
			max-width: 828px;

			z-index: get-layer('nav-expanded-menu');
			@include respond-below('phone') {
				width: 100%;
				max-width: initial;
			}
		}

		&-content {
			animation: slide-content-enter 600ms forwards;
			animation-delay: 300ms;
			animation-timing-function: cubic-bezier(0.17, 0.84, 0.44, 1);
			padding: $space;

			opacity: 0;
			transform: translateX(20%);

			display: flex;
			flex-direction: column;
			gap: 2rem;
			height: 100%;
			overflow-y: auto;

			& > * {
				order: 100;
			}

			@keyframes slide-content-enter {
				to {
					transform: translateX(0%);
					opacity: 1;
				}
			}
		}

		&-header {
			display: flex;
			flex-direction: column;
			gap: 1rem 0;

			margin-right: auto;

			@include respond-below('phone') {
				order: 109;
			}
		}

		&-sosial-media {
			position: relative;
			display: flex;
			justify-content: flex-start;
			align-items: center;
			gap: 0 0.5rem;
			margin-right: 1.5rem;

			&:hover {
				text-decoration: underline;
			}

			.icon-wrapper {
				$size: 21px;
				height: $size;
				width: $size;
			}

			&-wrapper {
				padding-left: $spaceLeft;
				@include respond-below('phone') {
					padding: 0;
					order: 110;
				}
			}
		}

		&-link-title {
			@extend .text-micro;
			margin-top: 0.5rem;

			@include respond-below('phone') {
				font-size: 20px !important;
			}
		}

		&-links {
			display: grid;
			grid-gap: 1.3rem 1rem;
			grid-template-columns: repeat(5, 1fr);
			margin: auto 0;

			@include respond-below('phone') {
				grid-template-columns: 1fr;
				grid-gap: 1rem 0rem;
			}
		}

		&-top-links {
			padding-left: $spaceLeft;
			&:hover {
				text-decoration: underline;
			}
			@include respond-below('phone') {
				padding: 0;
				font-size: 20px !important;
			}
		}

		&-hr {
			display: none;
			color: $primary;
			text-align: left;
			margin: 0;
			height: 1px;
			width: 3rem;
			@include respond-below('phone') {
				display: initial;
			}
		}
	}
}

.content-slide-enter-active,
.content-slide-leave-active {
	transition-property: transform;
	transition-duration: 400ms;
	transition-timing-function: cubic-bezier(0.17, 0.84, 0.44, 1);
}

.content-slide-enter, .content-slide-leave-to /* .fade-leave-active below version 2.1.8 */ {
	transform: translateX(100%);
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
	transition: opacity 600ms;
}
.overlay-fade-enter, .overlay-fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
	opacity: 0;
}
</style>
